import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { fmtIDR, todayISO } from "@/lib/format";
import { BookOpen, Download } from "lucide-react";
import { API } from "@/lib/api";

const BukuBesar = () => {
  const [start, setStart] = useState(new Date().toISOString().slice(0, 7) + "-01");
  const [end, setEnd] = useState(todayISO());
  const [account, setAccount] = useState("all");
  const [coa, setCoa] = useState([]);
  const [ledger, setLedger] = useState([]);

  useEffect(() => { api.get("/coa").then((r) => setCoa(r.data)); }, []);
  useEffect(() => {
    const params = { start, end };
    if (account !== "all") params.account = account;
    api.get("/buku-besar", { params }).then((r) => setLedger(r.data));
  }, [start, end, account]);

  const download = (fmt) => {
    const token = localStorage.getItem("spbu_token");
    const outlet = localStorage.getItem("spbu_active_outlet") || "default";
    fetch(`${API}/export/buku-besar.${fmt}?start=${start}&end=${end}`, { headers: { Authorization: `Bearer ${token}`, "X-Outlet-Id": outlet } })
      .then(r => r.blob()).then(b => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = `buku_besar.${fmt}`; a.click();
      });
  };

  return (
    <div className="space-y-6" data-testid="buku-besar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" data-testid="buku-besar-title">Buku Besar</h1>
          <p className="text-sm text-slate-500 mt-1">General Ledger — jurnal per akun COA</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">Dari</Label><Input data-testid="bb-start-date" type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div><Label className="text-xs">Sampai</Label><Input data-testid="bb-end-date" type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          <div className="w-64">
            <Label className="text-xs">Akun</Label>
            <Select value={account} onValueChange={setAccount}>
              <SelectTrigger data-testid="bb-account-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="bb-account-all">Semua Akun</SelectItem>
                {coa.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`bb-account-${c.code}`}>{c.code} — {c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => download("pdf")} data-testid="bb-pdf-button"><Download className="w-4 h-4 mr-2" />PDF</Button>
          <Button className="bg-[#005DAA] hover:bg-[#004885]" onClick={() => download("xlsx")} data-testid="bb-excel-button"><Download className="w-4 h-4 mr-2" />Excel</Button>
        </div>
      </div>

      {ledger.length === 0 && (
        <Card data-testid="bb-empty-state"><CardContent className="p-8 text-center text-slate-500">
          <BookOpen className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          Belum ada transaksi pada periode ini.
        </CardContent></Card>
      )}

      <div className="space-y-4">
        {ledger.map((a) => (
          <Card key={a.code} data-testid={`bb-account-card-${a.code}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base font-bold flex items-center gap-2">
                  <span className="font-mono-fig text-xs text-slate-400">{a.code}</span>
                  {a.name}
                  <Badge variant="secondary" className="text-xs">{a.type}</Badge>
                </CardTitle>
                <div className="flex gap-4 text-sm">
                  <span className="text-slate-500">Total Debit: <span className="font-mono-fig font-bold text-slate-800" data-testid={`bb-total-debit-${a.code}`}>{fmtIDR(a.total_debit)}</span></span>
                  <span className="text-slate-500">Total Kredit: <span className="font-mono-fig font-bold text-slate-800" data-testid={`bb-total-credit-${a.code}`}>{fmtIDR(a.total_credit)}</span></span>
                  <span className="text-slate-700">Saldo: <span className="font-mono-fig font-extrabold text-[#E30613]" data-testid={`bb-ending-balance-${a.code}`}>{fmtIDR(a.ending_balance)}</span></span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Keterangan</TableHead>
                  <TableHead>Ref</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Kredit</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {a.entries.map((e, i) => (
                    <TableRow key={i} data-testid={`bb-entry-${a.code}-${i}`}>
                      <TableCell className="font-mono-fig text-xs">{e.date}</TableCell>
                      <TableCell className="text-sm">{e.description}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs capitalize">{e.ref_type}</Badge></TableCell>
                      <TableCell className="text-right font-mono-fig text-sm">{e.debit > 0 ? fmtIDR(e.debit) : "-"}</TableCell>
                      <TableCell className="text-right font-mono-fig text-sm">{e.credit > 0 ? fmtIDR(e.credit) : "-"}</TableCell>
                      <TableCell className="text-right font-mono-fig text-sm font-semibold">{fmtIDR(e.balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default BukuBesar;
